import { yupResolver } from "@hookform/resolvers/yup";
import { Divider } from "@mui/material";
import { format } from "date-fns";
import csLocale from "date-fns/locale/cs";
import { type FC } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import * as yup from "yup";
import { RadioGroup } from "../../../components/Form/RadioGroup";
import { RadioInput } from "../../../components/Form/RadioInput";
import { useTypedTranslation } from "../../../hooks/useTypedTranslation";
import { INFLATION_PERCENT } from "../InflationStepper.helpers";
import * as Styled from "../InflationStepper.styled";
import { InflationResult, InflationStepperProps } from "../InflationStepper.types";

type FormValues = {
    inflation?: string | null;
    gio?: string | null;
};

type FormProps = InflationStepperProps & {
    setFormResult: React.Dispatch<React.SetStateAction<InflationResult | null>>;
};

const booleanToString = (value: boolean | undefined) => (value ? "true" : "false");

export const Form: FC<FormProps> = ({ inflationData, setPageIndex, setFormResult }) => {
    const { t } = useTypedTranslation();

    const showGio = inflationData?.indexOptionOfferFlag ?? false;
    const gio = inflationData?.indexOptionFlag ?? false;

    const showInflation =
        (inflationData?.independentIncreaseOfferFlag || inflationData?.inflationIncreaseOfferFlag) ?? false;
    const inflation = (inflationData?.independentIncreaseFlag || inflationData?.inflationIncreaseFlag) ?? false;

    const baseValidation = yup.string().oneOf(["true", "false"], "Musíte vybrat hodnotu");

    const formSchema: yup.ObjectSchema<FormValues> = yup.object({
        inflation: showInflation
            ? baseValidation.required("Povinne pole!")
            : baseValidation
                  .optional()
                  .nullable()
                  .transform((v) => (v === null ? undefined : v)),
        gio: showGio
            ? baseValidation.required("Povinne pole!")
            : baseValidation
                  .optional()
                  .nullable()
                  .transform((v) => (v === null ? undefined : v)),
    });

    const methods = useForm<FormValues>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            inflation: showInflation ? booleanToString(inflation) : undefined,
            gio: showGio ? booleanToString(gio) : undefined,
        },
        // Enable validation + isValid update on first field change so button activates after single selection
        mode: "onChange",
        reValidateMode: "onChange",
    });

    const handleContinue = (values: FormValues) => {
        const result: InflationResult = {
            inflation: showInflation ? values.inflation === "true" : undefined,
            gio: showGio ? values.gio === "true" : undefined,
        };
        setFormResult(result);
        setPageIndex?.(1); // move to recap page
    };

    // const continueButtonDisabled = !(methods.formState.isDirty && methods.formState.isValid);
    const continueButtonDisabled = !methods.formState.isValid;

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleContinue)}>
                <Styled.Wrapper>
                    {showInflation && (
                        <>
                            <Styled.SectionHeading>{t("inflationStepper.whyIncrease")}</Styled.SectionHeading>
                            <Styled.Description>
                                <Trans
                                    i18nKey="inflationStepper.whyIncreaseDescription"
                                    components={{ strong: <strong /> }}
                                />
                            </Styled.Description>
                        </>
                    )}

                    {showGio && <Divider sx={{ my: 3 }} />}

                    {showInflation && (
                        <Styled.OfferSection>
                            <Styled.OfferTitle>
                                {t("inflationStepper.offer1", {
                                    percent: INFLATION_PERCENT,
                                    date: inflationData?.anniversaryDate
                                        ? format(new Date(inflationData.anniversaryDate), "d. M. yyyy", {
                                              locale: csLocale,
                                          })
                                        : t("global.NA"),
                                })}
                            </Styled.OfferTitle>
                            <RadioGroup name="inflation">
                                <RadioInput
                                    id="inflation-offer1-yes"
                                    key="inflation-offer1-yes"
                                    name="inflation"
                                    value="true"
                                    label={t("global.iAgree")}
                                />
                                <RadioInput
                                    id="inflation-offer1-no"
                                    key="inflation-offer1-no"
                                    name="inflation"
                                    value="false"
                                    label={t("global.iDisagree")}
                                />
                            </RadioGroup>
                        </Styled.OfferSection>
                    )}

                    {showGio && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Styled.OfferSection>
                                <Styled.OfferTitle>{t("inflationStepper.offer2")}</Styled.OfferTitle>
                                <RadioGroup name="gio">
                                    <RadioInput
                                        id="inflation-offer2-yes"
                                        key="inflation-offer2-yes"
                                        name="gio"
                                        value="true"
                                        label={t("global.iAgree")}
                                    />
                                    <RadioInput
                                        id="inflation-offer2-no"
                                        key="inflation-offer2-no"
                                        name="gio"
                                        value="false"
                                        label={t("global.iDisagree")}
                                    />
                                </RadioGroup>
                            </Styled.OfferSection>
                        </>
                    )}
                </Styled.Wrapper>
                <Styled.ContinueButtonWrapper>
                    <Styled.ContinueButton disabled={continueButtonDisabled} type="submit">
                        {t("inflationStepper.continue")}
                    </Styled.ContinueButton>
                </Styled.ContinueButtonWrapper>
            </form>
        </FormProvider>
    );
};
